import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  Package,
  Package2,
  Truck,
  ShoppingCart,
  Users,
  Receipt,
  Wallet,
  UserCog,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Globe,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  History,
  ChefHat,
  Building2,
} from 'lucide-react';

const navItems = [
  { key: 'dashboard', icon: LayoutDashboard, path: '/' },
  { key: 'products', icon: Package, path: '/products' },
  { key: 'materials', icon: Package2, path: '/materials', roles: ['admin', 'accountant', 'sales'] },
  { key: 'suppliers', icon: Truck, path: '/suppliers', roles: ['admin', 'accountant', 'sales'] },
  { key: 'purchases', icon: ShoppingCart, path: '/purchases', roles: ['admin', 'accountant', 'sales'] },
  { key: 'customers', icon: Users, path: '/customers' },
  { key: 'sales', icon: Receipt, path: '/sales' },
  { key: 'dailyOrders', icon: ChefHat, path: '/daily-orders' },
  { key: 'expenses', icon: Wallet, path: '/expenses', roles: ['admin', 'accountant', 'sales'] },
  { key: 'balanceSheet', icon: Building2, path: '/balance-sheet', roles: ['admin', 'accountant'] },
  { key: 'employees', icon: UserCog, path: '/employees', roles: ['admin', 'accountant'] },
  { key: 'reports', icon: BarChart3, path: '/reports' },
  { key: 'users', icon: ShieldCheck, path: '/users', roles: ['admin'] },
  { key: 'activityLogs', icon: History, path: '/activity-logs', roles: ['admin'] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, toggleLanguage, language, isRTL } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter(
    (item) => !item.roles || item.roles.includes(user?.role)
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 h-16 glass border-b border-border/50 flex items-center justify-between px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </Button>
        <h1 className="font-heading text-xl text-primary">Qassioun Distribution</h1>
        <Button variant="ghost" size="icon" onClick={toggleLanguage} data-testid="language-toggle-mobile">
          <Globe size={20} />
        </Button>
      </header>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 h-full bg-card border-e border-border/50 z-50 transition-all duration-300',
          sidebarOpen ? 'w-64' : 'w-20',
          isRTL ? 'right-0' : 'left-0',
          'hidden lg:block',
          mobileMenuOpen && 'block lg:block'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-center border-b border-border/50 px-4">
            {sidebarOpen ? (
              <div className="flex items-center gap-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_877cf3ae-79e1-4b85-af95-e3f38fcbc2fb/artifacts/imx8202j_909ff97a-29cc-4bc8-9a25-59a8865473f7-300x294.png" 
                  alt="Logo" 
                  className="w-10 h-10 object-contain"
                />
                <h1 className="font-heading text-xl text-primary">Qassioun Distribution</h1>
              </div>
            ) : (
              <img 
                src="https://customer-assets.emergentagent.com/job_877cf3ae-79e1-4b85-af95-e3f38fcbc2fb/artifacts/imx8202j_909ff97a-29cc-4bc8-9a25-59a8865473f7-300x294.png" 
                alt="Logo" 
                className="w-10 h-10 object-contain"
              />
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.key}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`nav-${item.key}`}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200',
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                      : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                  )}
                >
                  <Icon size={20} className={cn(!sidebarOpen && 'mx-auto')} />
                  {sidebarOpen && <span className="font-medium">{t(item.key)}</span>}
                </Link>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-border/50">
            {sidebarOpen && (
              <div className="mb-4 px-2">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                size={sidebarOpen ? 'default' : 'icon'}
                onClick={toggleLanguage}
                className="w-full justify-start"
                data-testid="language-toggle"
              >
                <Globe size={18} />
                {sidebarOpen && <span className="ms-2">{language === 'en' ? 'العربية' : 'English'}</span>}
              </Button>
              <Button
                variant="ghost"
                size={sidebarOpen ? 'default' : 'icon'}
                onClick={handleLogout}
                className="w-full justify-start text-destructive hover:text-destructive"
                data-testid="logout-btn"
              >
                <LogOut size={18} />
                {sidebarOpen && <span className="ms-2">{t('logout')}</span>}
              </Button>
            </div>
          </div>

          {/* Collapse Toggle */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:flex absolute -end-3 top-20 w-6 h-6 bg-primary text-primary-foreground rounded-full items-center justify-center shadow-lg hover:scale-110 transition-transform"
            data-testid="sidebar-toggle"
          >
            {isRTL ? (
              sidebarOpen ? <ChevronRight size={14} /> : <ChevronLeft size={14} />
            ) : (
              sidebarOpen ? <ChevronLeft size={14} /> : <ChevronRight size={14} />
            )}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={cn(
          'min-h-screen transition-all duration-300 pt-16 lg:pt-0',
          isRTL
            ? sidebarOpen ? 'lg:mr-64' : 'lg:mr-20'
            : sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'
        )}
      >
        <div className="p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
